// SETTINGS V2 — reusable overlay shell (SETTINGS_V2_SP2 §7.1). The ONE proven
// dialog shell every v2 section dialog reuses (SP3 contact, SP4 location, SP6
// invite ×2, SP9 shipping); ChangePasswordDialogV2 is its first consumer.
//
// Mechanics ported from customer/midas/components/v3/ConfirmDialogV3 + the built
// nav/SettingsV2PhoneNavSheet: portal → document.body, scope-class wrapper with
// transparent background so the scoped CSS vars resolve inside, Escape + backdrop
// close, manual Tab focus trap + cached-activeElement focus restore,
// useHistoryBackClose. Visuals from designer SDialog (settings-kit.jsx 155-173).
//
// PM RULING (§7.1): portal to document.body — the profile cards' retained
// sv2-card-reveal end transform creates a CSS containing block that would trap a
// non-portalled position:fixed overlay; the layout's portal-rule permits this.
// zIndex 1300 (NOT 1500) — the shared ActionToast paints at fixed 1500 and must
// stack ABOVE the dialog (legacy MUI Dialog 1300 / Snackbar 1400). Theme from
// Redux (NO DOM sniff). Inline styles + scoped CSS-var tokens + shared Loader; no
// MUI/sx/dt/Tailwind. SEC-REQ-2/3: no import-time side effects.
import React, { useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import Loader from "../../../../shared/components/Loader";
import useHistoryBackClose from "../useHistoryBackClose";

export interface SettingsV2DialogShellProps {
  open: boolean;
  /** Escape, backdrop, ×, hardware Back all route here. */
  onClose: () => void;
  title: string;
  sub?: string;
  /** Desktop panel width, default 560. */
  width?: number;
  /** Mono footer caption (left side). */
  footerNote?: string;
  /** Action buttons (right side). */
  footer?: React.ReactNode;
  children: React.ReactNode;
  /** aria-labelledby target id (the title element). */
  labelledById: string;
  /** When true, children are replaced by <Loader/> and the footer is hidden. */
  busy?: boolean;
}

const SettingsV2DialogShell: React.FC<SettingsV2DialogShellProps> = ({
  open,
  onClose,
  title,
  sub,
  width = 560,
  footerNote,
  footer,
  children,
  labelledById,
  busy,
}) => {
  const theme = useAppSelector((s) => s.settingsV2Ui.theme);
  const { isPhone } = useBreakpoint();

  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const cachedTriggerRef = useRef<HTMLElement | null>(null);

  // Hardware/gesture Back closes the dialog (SP1.1 / Q-SETV2-6 = B). Pure dialog
  // with no internal navigation, so the hook's replace-consumer contract is a
  // no-op here — dismiss/Back/Escape all resolve through onClose.
  useHistoryBackClose(open, onClose);

  // Focus: cache the trigger on open, move focus to the first input (fall back
  // to the close button) on next tick; restore the trigger on close.
  useEffect(() => {
    if (open) {
      cachedTriggerRef.current =
        (document.activeElement as HTMLElement | null) || null;
      const id = window.setTimeout(() => {
        const target =
          panelRef.current?.querySelector<HTMLElement>("input") ||
          closeBtnRef.current;
        target?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
    const el = cachedTriggerRef.current;
    if (el && typeof el.focus === "function") el.focus();
    cachedTriggerRef.current = null;
    return undefined;
  }, [open]);

  // Keyboard: Escape → close; Tab/Shift-Tab cycle within the panel only.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const root = panelRef.current;
        if (!root) return;
        const focusables = Array.from(
          root.querySelectorAll<HTMLElement>(
            'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [open, onClose]
  );

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open || typeof document === "undefined") return null;

  // Panel geometry: desktop/tablet = centered rounded card; phone = fullscreen.
  const panelStyle: React.CSSProperties = isPhone
    ? {
        position: "absolute",
        inset: 0,
        width: "100%",
        borderRadius: 0,
        maxHeight: "none",
        background: "var(--card)",
        border: "1px solid var(--border)",
        boxShadow: "none",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }
    : {
        position: "relative",
        width: `min(${width}px, calc(100vw - 32px))`,
        background: "var(--card)",
        borderRadius: 14,
        border: "1px solid var(--border)",
        boxShadow:
          "0 24px 64px rgba(15,23,30,0.22), 0 4px 16px rgba(15,23,30,0.10)",
        overflow: "hidden",
        maxHeight: "calc(100vh - 32px)",
        display: "flex",
        flexDirection: "column",
        animation: "sv2-fade-up 0.25s ease-out",
      };

  const node = (
    <div
      className={`settings-v2-root p-${theme} mod-nastavenia`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        background: "transparent",
        display: "grid",
        placeItems: "center",
      }}
      role="presentation"
      onMouseDown={(e) => {
        // Backdrop click → close (only fires on the wrapper itself).
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(15, 23, 30, 0.32)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          animation: "sv2-fade-in 0.2s ease-out",
        }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        onMouseDown={(e) => e.stopPropagation()}
        style={panelStyle}
      >
        {/* Header (pinned) */}
        <div
          style={{
            flex: "0 0 auto",
            padding: "18px 22px 0",
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              id={labelledById}
              style={{
                fontSize: 16.5,
                fontWeight: 600,
                color: "var(--ink)",
                letterSpacing: "-0.01em",
              }}
            >
              {title}
            </div>
            {sub && (
              <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 2 }}>
                {sub}
              </div>
            )}
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="btn btn-ghost"
            aria-label={title}
            onClick={onClose}
            style={{ width: 30, padding: 0, fontSize: 15, color: "var(--muted)" }}
          >
            ×
          </button>
        </div>

        {/* Body (scrolls) */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "14px 22px 4px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {busy ? (
            <div style={{ minHeight: 200, display: "grid", placeItems: "center" }}>
              <Loader />
            </div>
          ) : (
            children
          )}
        </div>

        {/* Footer (pinned) — hidden while busy. R1 fix: the note flexes + wraps
            within its own box (flex:1 + minWidth:0) while the action buttons live
            in a flex-shrink:0 group so they keep their natural width and never
            wrap/crowd. flexWrap lets the note drop above the buttons on very narrow
            phone widths for breathing room. `.btn` also carries white-space:nowrap. */}
        {!busy && (footer || footerNote) && (
          <div
            style={{
              flex: "0 0 auto",
              padding: "12px 22px 16px",
              marginTop: 8,
              borderTop: "1px solid var(--border-soft)",
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            {footerNote ? (
              <span
                style={{
                  flex: "1 1 200px",
                  minWidth: 0,
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  lineHeight: 1.5,
                  color: "var(--faint)",
                  letterSpacing: "0.04em",
                }}
              >
                {footerNote}
              </span>
            ) : (
              <span style={{ flex: 1 }} />
            )}
            {footer && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexShrink: 0,
                  marginLeft: "auto",
                }}
              >
                {footer}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
};

export default SettingsV2DialogShell;
